/**
 * The configuration surface, organised by CADENCE (ORDR-119, FR-CFM-004).
 *
 * Ruling B9: the grouping is a HARD-CODED table, FRS-55 section 55.3, quoted
 * verbatim here and encoded below. Do not reword it.
 *
 * | Module | Who and when | Groups |
 * |---|---|---|
 * | **Brand setup** | brand, at onboarding, rarely after | `MENU_CONTENT`, `MENU_STRUCTURE`, `NUTRITION_ALLERGEN`, `BRAND_THEME`, `SPLASH`, `NOTIFICATION_TEMPLATES`, `RESTRICTED_GOODS`, `ASSISTANT`, `CHANNELS` |
 * | **Brand ongoing** | brand, continually | menu versions, `PROMOTIONS`, `BASE_PRICE` where locked, `SPLASH` |
 * | **Location setup** | location, once, then on change | `HOURS`, `FULFILMENT_MODES`, `MODE_PROFILE`, `STATION_ROUTING`, `DELIVERY_ZONES`, `DELIVERY_FEES`, `COURIER_POLICY`, `TAX_PROFILE`, `ORDER_THRESHOLDS`, `UNATTENDED_POLICY`, `ACCEPTANCE`, `NOTIFICATIONS`, `STAFF_ACCESS` |
 * | **Location daily** | location, during service | `EIGHTYSIX`, `ITEM_AVAILABILITY`, `CAPACITY` coefficient, `MENU_SCHEDULE`, temporary delay under FR-ORD-020 |
 *
 * THE TABLE NAMES GROUPS, NOT DOMAINS. Only a `ConfigDomain` member can be
 * fetched (the GET answers any other name with 400), so `domain` is set ONLY
 * for those, and every other entry keeps its label and says it has no home on
 * this plane yet. It is never fetched.
 */

import type { ConfigDomain } from "./domains.js";

export interface CadenceEntry {
  /** The table's own words, backticks dropped. */
  readonly label: string;
  /** Set only when the group is a `CONFIG_DOMAINS` member. */
  readonly domain: ConfigDomain | null;
  /** When set, only these keys of the domain belong to this entry. */
  readonly onlyKeys?: readonly string[];
  /** When set, these keys of the domain belong elsewhere. */
  readonly exceptKeys?: readonly string[];
  /** A sentence added under the "no home" line. */
  readonly note?: string;
}

export interface CadenceModule {
  readonly key: string;
  readonly title: string;
  readonly whoAndWhen: string;
  readonly entries: readonly CadenceEntry[];
}

const COEFFICIENT = ["capacity_coefficient_permille"] as const;

const d = (domain: ConfigDomain, label: string = domain): CadenceEntry => ({ label, domain });
const none = (label: string, note?: string): CadenceEntry =>
  note === undefined ? { label, domain: null } : { label, domain: null, note };

export const CADENCE_MODULES: readonly CadenceModule[] = [
  {
    key: "brand-setup",
    title: "Brand setup",
    whoAndWhen: "brand, at onboarding, rarely after",
    entries: [
      d("MENU_CONTENT"), d("MENU_STRUCTURE"), d("NUTRITION_ALLERGEN"), d("BRAND_THEME"),
      none("SPLASH"), d("NOTIFICATION_TEMPLATES"), none("RESTRICTED_GOODS"),
      none("ASSISTANT"), none("CHANNELS"),
    ],
  },
  {
    key: "brand-ongoing",
    title: "Brand ongoing",
    whoAndWhen: "brand, continually",
    entries: [
      none("menu versions"), d("PROMOTIONS"), d("BASE_PRICE", "BASE_PRICE where locked"), none("SPLASH"),
    ],
  },
  {
    key: "location-setup",
    title: "Location setup",
    whoAndWhen: "location, once, then on change",
    entries: [
      d("HOURS"), d("FULFILMENT_MODES"), none("MODE_PROFILE"), d("STATION_ROUTING"),
      d("DELIVERY_ZONES"), d("DELIVERY_FEES"), d("COURIER_POLICY"), d("TAX_PROFILE"),
      none("ORDER_THRESHOLDS"), none("UNATTENDED_POLICY"),
      none("ACCEPTANCE", "The acceptance policy is the CAPACITY key `acceptance_policy`."),
      none("NOTIFICATIONS"), none("STAFF_ACCESS"),
    ],
  },
  {
    key: "location-daily",
    title: "Location daily",
    whoAndWhen: "location, during service",
    entries: [
      d("EIGHTYSIX"), none("ITEM_AVAILABILITY"),
      { label: "CAPACITY coefficient", domain: "CAPACITY", onlyKeys: COEFFICIENT },
      none("MENU_SCHEDULE"), none("temporary delay under FR-ORD-020"),
    ],
  },
];

/** Every domain the table does not name, so nothing is unreachable. */
export const OTHER_SECTION: CadenceModule = {
  key: "other",
  title: "Not placed by FRS-55 section 55.3",
  whoAndWhen: "Other settings",
  entries: [
    d("LOYALTY_PROGRAMME"), d("SETTLEMENT"), d("PREP_TIMES"),
    { label: "CAPACITY, apart from the coefficient", domain: "CAPACITY", exceptKeys: COEFFICIENT },
  ],
};

/** The keys an entry is responsible for, from `onlyKeys` / `exceptKeys`. */
export function keysOf(entry: CadenceEntry): (key: string) => boolean {
  const { onlyKeys, exceptKeys } = entry;
  return (key) => (onlyKeys === undefined || onlyKeys.includes(key)) && !(exceptKeys ?? []).includes(key);
}

export function moduleDomains(module: CadenceModule): ConfigDomain[] {
  return module.entries.flatMap((entry) => (entry.domain === null ? [] : [entry.domain]));
}
